import {
  ApplicationInsights,
  IExtendedConfiguration,
  IExtendedTelemetryItem,
  IPropertyConfiguration,
  IWebAnalyticsConfiguration,
  PropertiesPlugin
} from '@microsoft/1ds-analytics-web-js';
import { QosPlugin, IQosConfiguration } from '@microsoft/1ds-qos-js';
import { QosResultData } from '../types/event-schemas/IQosResultData';
import { IWarnData } from '../types/event-schemas/IWarnData';
import { IPerformanceData } from '../types/event-schemas/IPerformanceData';
import { ICustomData } from '../types/event-schemas';
import { ILogEvent } from '../types/ILogEvent';
import { ITelemetrySink } from '../types/ITelemetrySink';
import { EventType } from '../types/EventType';
import { getErrorJsonData } from '../utils/getErrorJsonData';
import { stringifyHelper } from '../utils/stringifyHelper';
import { scrubUnknownEventType, scrubSensitiveData } from '../utils/scrubSensitiveData';
import { ITelemetrySinkFactoryParams, TelemetrySinkFactory } from '../types/TelemetrySinkFactory';
/**
 * Configuration for initialization
 */
export interface IOneDsSinkConfig {
  /**
   * The unique guid got from 1DS onboarding process
   */
  readonly instrumentKey: string;
  /**
   * Determine if the telemetry should be send to the 1DS backend
   * If not, it will be sent to the console
   */
  readonly enablePublishTelemetry: boolean;
  /**
   * Determine if the telemetry data will be printed to console
   */
  readonly enableConsolePrinting: boolean;
  /**
   * When set true, network call will be auto-tracked
   */
  readonly enableNetworkCallTracking: boolean;
  /**
   * 1DS backend endpoint where telemetry data is sent
   */
  readonly endpointUrl?: string;
}

/**
 * Create a factory to initiate OneDsTelemetry sink
 */
export const createFactory = (oneDsParams: IOneDsSinkConfig): TelemetrySinkFactory => {
  return (factoryParams: ITelemetrySinkFactoryParams) => {
    return new OneDsTelemetrySink(oneDsParams, factoryParams);
  };
};

/**
 * Telemetry sink that uses 1DS SDK to send telemetry data to the 1DS pipeline
 */
class OneDsTelemetrySink implements ITelemetrySink {
  public id = 'OneDsTelemetrySink';
  private _appInsights = new ApplicationInsights();
  private _qosPlugin = new QosPlugin();
  private _propertiesPlugin = new PropertiesPlugin();
  constructor(config: IOneDsSinkConfig, factoryParams: ITelemetrySinkFactoryParams) {
    this._initializeTelemetry(config, factoryParams);
  }

  private _initializeTelemetry = (initialConfig: IOneDsSinkConfig, factoryParams: ITelemetrySinkFactoryParams) => {
    const { instrumentKey, enablePublishTelemetry, enableConsolePrinting, enableNetworkCallTracking, endpointUrl } =
      initialConfig;
    // Configure ApplicationInsights
    const webAnalyticsConfig: IWebAnalyticsConfiguration = {
      autoCapture: {
        onLoad: false,
        onUnload: false,
        click: false,
        jsError: false,
        pageView: true
      },
      useDefaultContentName: false,
      urlCollectHash: true
    };

    // Add part A properties: https://msasg.visualstudio.com/Shared%20Data/_git/1DS.JavaScript?path=%2Fextensions%2Fproperties
    const propertiesConfig: IPropertyConfiguration = {
      dropIdentifiers: true,
      hashIdentifiers: true,
      scrubIpOnly: true,
      populateBrowserInfo: true,
      populateOperatingSystemInfo: true,
      gpcDataSharingOptIn: false
    };

    const config: IExtendedConfiguration = {
      instrumentationKey: instrumentKey,
      // Extra extensions
      extensions: [this._qosPlugin, this._propertiesPlugin],
      webAnalyticsConfiguration: webAnalyticsConfig,
      propertyConfiguration: propertiesConfig,
      extensionConfig: {},
      endpointUrl
    };

    // Config Ajax/Fetch auto tracking: https://msasg.visualstudio.com/Shared%20Data/_git/1DS.JavaScript?path=%2Fextensions%2FqosAnalytics%2FREADME.md&version=GBmaster&fullScreen=true&_a=preview
    const qosConfig: IQosConfiguration = {
      disableAjaxTracking: !enableNetworkCallTracking,
      enableAjaxPerfTracking: enableNetworkCallTracking,
      enableAjaxErrorStatusText: enableNetworkCallTracking
    };

    if (config.extensionConfig) {
      config.extensionConfig[this._qosPlugin.identifier] = qosConfig;
    }

    if (config.extensionConfig) {
      config.extensionConfig[this._propertiesPlugin.identifier] = propertiesConfig;
    }

    // Initialize SDK
    this._appInsights.initialize(config, []);

    // Modify the content of telemetry data before send it
    this._appInsights.addTelemetryInitializer((item: IExtendedTelemetryItem) => {
      if (!item.baseData) {
        item.baseData = {};
      }
      if (!item.data) {
        item.data = {};
      }

      // Get the latest context properties
      item.data = { ...item.data, ...factoryParams.getContextData() };

      try {
        const isKnownEventType = Object.values(EventType).includes(item.name as EventType);
        item.baseData = this.sanitizeData({ ...item.baseData }, isKnownEventType);
        item.data = this.sanitizeData({ ...item.data }, isKnownEventType);
      } catch (e) {
        this.error({
          name: 'Data scrub error',
          data: e
        });

        return false;
      }

      if (enableConsolePrinting) {
        console.log(item.name, item);
      }

      if (!enablePublishTelemetry) {
        // The log won't be sent
        return false;
      }

      return true;
    });
  };

  public reportCustomEvent = (logEvent: ILogEvent<ICustomData>): void => {
    const { name, data, context } = logEvent;
    this._appInsights.trackEvent({
      name: EventType.CustomEvent,
      data: {
        name,
        message: stringifyHelper(data), // Stringify the data so that it can be stored in a single column of each Custom event
        ...context
      } as any // The 'data' property doesn't support 'undefined' type. Take the advice from 1DS team to cast it to 'any' type to allow logging optional data.
    });
  };

  public reportPerformance = (logEvent: ILogEvent<IPerformanceData>): void => {
    const { name, data, context } = logEvent;
    this._appInsights.trackEvent({
      name: EventType.Performance,
      data: {
        name,
        details: stringifyHelper(data), // Stringify the data so that it can be stored in a single column of each Performance event
        ...context
      } as any
    });
  };

  public reportQos = (logEvent: ILogEvent<QosResultData>): void => {
    const { name, data, context } = logEvent;
    this._appInsights.trackEvent({
      name: EventType.Qos,
      data: {
        name,
        ...data,
        ...context
      } as any
    });
  };

  public warn = (logEvent: ILogEvent<IWarnData>): void => {
    const { name, data, context } = logEvent;
    this._appInsights.trackEvent({
      name: EventType.Warn,
      data: {
        name,
        ...data,
        ...context
      }
    } as any);
  };

  public error = (logEvent: ILogEvent<unknown>): void => {
    const { name, data, context } = logEvent;
    // Not return the Error instance, otherwise it cannot be stringified by 1DS
    const error = getErrorJsonData(data);

    // The built-in trackException API cannot be used as it might generate array fields that cannot be propagated to Interchange/Geneva
    // This is known limitation and the Interchange team has the plan to fix it
    this._appInsights.trackEvent({
      name: EventType.Error,
      data: {
        name,
        message: error.message,
        stack: error.stack,
        ...context
      } as any
    });
  };

  /**
   * Remove sensitiveData property and JWT token. Should be done in all the sinks for security reason.
   * TODO: sanitize data before passing to each sink for security purpose
   */
  private sanitizeData = (data: any, isKnownEventType?: boolean) => {
    data = stringifyHelper(data);
    if (!isKnownEventType) {
      data = scrubUnknownEventType(data);
    }
    return JSON.parse(scrubSensitiveData(data));
  };
}
